import { useState } from "react";
import API from "snudbud-backbone-sdk";
import handleError from "../../../utils/handleError";
import { createAudioFile } from "../../../utils/media";
import { useAudioRecorder } from "../../../utils/useAudioRecorder";

interface MessageInputProps {
  userId?: string;
  api: API;
}

export default function MessageInput({ userId, api }: MessageInputProps) {
  const [messageInput, setMessageInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);

  // Use the audio recorder hook
  const [
    { isRecording, recordingDuration, audioBlob },
    { startRecording, stopRecording, cancelRecording, resetRecording },
  ] = useAudioRecorder();

  // Handle message input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessageInput(e.target.value);
  };

  // Format recording time (MM:SS)
  const formatRecordingTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Send voice message
  const sendVoiceMessage = async () => {
    if (!userId || !audioBlob || isProcessingAudio) return;

    setIsProcessingAudio(true);

    try {
      // Create file using utility
      const file = createAudioFile(audioBlob);

      // Send via socket
      api.socket?.emit("chat:message-voice", file, { userId });

      // Reset recording
      resetRecording();
    } catch (error) {
      handleError(error);
    } finally {
      setIsProcessingAudio(false);
    }
  };

  // Handle sending a message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId || !messageInput.trim() || isSending) return;

    setIsSending(true);

    try {
      // Emit to socket for real-time delivery
      api.socket?.emit("chat:message", { content: messageInput.trim(), userId });

      // Clear input
      setMessageInput("");
    } catch (error) {
      handleError(error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="p-4 border-t bg-white">
      {isRecording || audioBlob ? (
        <div className="flex items-center">
          {isRecording ? (
            <>
              <div className="flex-1 p-3 border border-gray-300 rounded-full flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse-record mr-3"></div>
                <span className="text-gray-700">Recording... {formatRecordingTime(recordingDuration)}</span>
              </div>
              <button
                onClick={stopRecording}
                className="ml-2 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 w-10 h-10 flex items-center justify-center"
                aria-label="Stop recording"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <rect x="6" y="6" width="12" height="12" rx="1" strokeWidth="2" />
                </svg>
              </button>
              <button
                onClick={cancelRecording}
                className="ml-2 bg-gray-500 text-white p-2 rounded-full hover:bg-gray-600 w-10 h-10 flex items-center justify-center"
                aria-label="Cancel recording"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </>
          ) : (
            <>
              <div className="flex-1 p-3 border border-gray-300 rounded-full flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                <span className="text-gray-700">
                  Audio message ready to send ({formatRecordingTime(recordingDuration)})
                </span>
              </div>
              <button
                onClick={sendVoiceMessage}
                disabled={isProcessingAudio}
                className="ml-2 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 disabled:bg-blue-300 w-10 h-10 flex items-center justify-center"
                aria-label="Send voice message"
              >
                {isProcessingAudio ? (
                  <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                )}
              </button>
              <button
                onClick={resetRecording}
                className="ml-2 bg-gray-500 text-white p-2 rounded-full hover:bg-gray-600 w-10 h-10 flex items-center justify-center"
                aria-label="Cancel voice message"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </>
          )}
        </div>
      ) : (
        <form onSubmit={handleSendMessage} className="flex items-center">
          <input
            type="text"
            value={messageInput}
            onChange={handleInputChange}
            placeholder="Type a message..."
            className="flex-1 p-3 border border-gray-300 rounded-full focus:ring-blue-500 focus:border-blue-500"
            disabled={isSending}
          />
          <button
            type="button"
            onClick={startRecording}
            className="ml-2 bg-gray-600 text-white p-2 rounded-full hover:bg-gray-700 w-10 h-10 flex items-center justify-center"
            aria-label="Record voice message"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            </svg>
          </button>
          <button
            type="submit"
            className="ml-2 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 disabled:bg-blue-300 w-10 h-10 flex items-center justify-center"
            disabled={!messageInput.trim() || isSending}
            aria-label="Send message"
          >
            {isSending ? (
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
